/**
 * Storage helpers for the in-app feedback prompt.
 *
 * Two flags govern visibility:
 * - snoozed-until (localStorage): timestamp before which the prompt stays quiet
 *   — 6 months after a dismissal without feedback, 1 year once the user has
 *   rated the app at least once.
 * - shown-this-session (sessionStorage): the prompt has already appeared in the
 *   current browser session — show at most once per connection.
 */
const SNOOZED_UNTIL_KEY = 'bill-alps:feedback-snoozed-until';
/** Legacy "never show again" flag, migrated to a snooze on first read. */
const LEGACY_DISMISSED_KEY = 'bill-alps:feedback-dismissed';
const SHOWN_SESSION_KEY = 'bill-alps:feedback-shown-session';
const CONNECTED_KEY = 'bill-alps:feedback-connected';

export type SnoozeKind = 'never-submitted' | 'submitted';

/** Milliseconds epoch before which the prompt must not appear; 0 when unset. */
export function getFeedbackSnoozedUntil(): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(SNOOZED_UNTIL_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isFeedbackSnoozed(): boolean {
  return Date.now() < getFeedbackSnoozedUntil();
}

/** 6 months without feedback, 1 year once the user has rated the app. */
export function snoozeFeedback(kind: SnoozeKind): void {
  if (typeof window === 'undefined') return;
  const until = new Date();
  if (kind === 'submitted') {
    until.setFullYear(until.getFullYear() + 1);
  } else {
    until.setMonth(until.getMonth() + 6);
  }
  window.localStorage.setItem(SNOOZED_UNTIL_KEY, String(until.getTime()));
}

/**
 * Convert a pre-snooze "never show again" choice into a timed snooze starting
 * now, so nobody stays silenced forever. Idempotent.
 */
export function migrateLegacyDismissal(kind: SnoozeKind): void {
  if (typeof window === 'undefined') return;
  if (window.localStorage.getItem(LEGACY_DISMISSED_KEY) !== '1') return;

  if (getFeedbackSnoozedUntil() === 0) snoozeFeedback(kind);
  window.localStorage.removeItem(LEGACY_DISMISSED_KEY);
}

export function hasFeedbackShownThisSession(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(SHOWN_SESSION_KEY) === '1';
}

export function markFeedbackShownThisSession(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(SHOWN_SESSION_KEY, '1');
}

/** True until the user has connected to the app at least once on this device. */
export function isFirstConnection(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(CONNECTED_KEY) !== '1';
}

export function markConnected(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CONNECTED_KEY, '1');
}
